import Mux from '@mux/mux-player-react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'validate.js';

import { pushParams } from 'dpl/actions/query_params';
import ClickableDiv from 'dpl/common/components/ClickableDiv';
import MediaCarouselSplide, {
  DEFAULT_SPLIDE_OPTIONS
} from 'dpl/common/components/MediaCarouselSplide';
import Badge from 'dpl/common/design_system/Badge';
import { COLOR_OPTIONS } from 'dpl/common/design_system/utils/constants';
import SmartImage from 'dpl/components/SmartImage';
import { ANALYTICS_EVENTS } from 'dpl/constants/analytics';
import { ANALYTICS } from 'dpl/constants/query_params';
import withLazyLoading from 'dpl/decorators/withLazyLoading';
import usePuppyPageVariant from 'dpl/puppy_page/hooks/usePuppyPageVariant';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import { PhotographPropTypes } from 'dpl/shared/utils/customPropTypes';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';

const COMMON_ARROW_CLASS_NAME = 'bg-white black f6';
const IS_LT_LG = isLessThanBreakpoint('lg');

export function CardMediaCarouselFile({
  badgeProps,
  mediaFile,
  smartImageProps,
  analytics,
  muxRef
}) {
  const puppyPageVariant = usePuppyPageVariant();

  const {
    alt,
    analytics: imageAnalytics,
    clickthrough_url: clickthroughUrl,
    url
  } = mediaFile;

  const dispatch = useDispatch();

  const dogModalId = mediaFile.dog_modal_id || {};
  const hasDogModal = !isEmpty(dogModalId);
  const puppyId = dogModalId?.puppy_id;
  const isVideo = mediaFile.type === 'Video';

  function handleImageClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (isVideo) {
      return;
    }

    if (imageAnalytics) {
      sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, imageAnalytics);
    }

    if (puppyPageVariant.enabled && puppyId) {
      openPuppyPage({ puppyId });
    } else if (hasDogModal) {
      const modalParams = { ...dogModalId, [ANALYTICS]: analytics };
      dispatch(pushParams(modalParams, { hash: true }));
    } else {
      window.location.href = clickthroughUrl;
    }
  }

  const wrapperClasses = 'CardMediaCarouselFile h-100 db relative';

  const carouselImage = !isVideo && (
    <>
      <SmartImage {...smartImageProps} alt={alt} src={url} />
      {!isEmpty(badgeProps) && (
        <div
          className={classnames(
            'absolute absolute--fill pointer-events-none pv3 ph3'
          )}
        >
          <Badge color={badgeProps.color} size="small">
            {badgeProps.text}
          </Badge>
        </div>
      )}
    </>
  );

  const carouselMediaFile = isVideo ? (
    <Mux
      ref={muxRef}
      streamType="on-demand"
      playbackId={mediaFile.playback_ids?.[0]?.id}
      style={{
        '--bottom-controls': 'none',
        '--center-seek-forward-button': 'none',
        '--center-seek-backward-button': 'none',
        '--bottom-play-button': 'none',
        '--bottom-volume-range': 'none',
        '--bottom-time-range:': 'none',
        '--bottom-time-display': 'none',
        '--bottom-duration-display': 'none',
        '--bottom-mute-button': 'none',
        '--bottom-captions-button': 'none',
        '--bottom-airplay-button': 'none',
        '--bottom-pip-button': 'none',
        '--bottom-playback-rate-button': 'none',
        width: smartImageProps.width,
        height: smartImageProps.height
      }}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
    />
  ) : (
    carouselImage
  );

  if (hasDogModal || clickthroughUrl || isVideo) {
    const fileType = isVideo ? 'video' : 'image';
    // HACK: using ClickableDiv since the outer Card is already an anchor
    return (
      <ClickableDiv
        data-test-id={`card-media-carousel-${fileType}-${mediaFile.id}`}
        className={wrapperClasses}
        style={{
          width: smartImageProps.width
        }}
        onClick={handleImageClick}
      >
        {carouselMediaFile}
      </ClickableDiv>
    );
  }

  return (
    <div
      className={wrapperClasses}
      data-test-id={`card-media-carousel-image-${mediaFile.id}`}
    >
      {carouselImage}
    </div>
  );
}

CardMediaCarouselFile.propTypes = {
  mediaFile: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    analytics: PropTypes.shape({
      view: PropTypes.string.isRequired,
      view_feature: PropTypes.string.isRequired,
      view_feature_location: PropTypes.string.isRequired
    }),
    url: PropTypes.string,
    alt: PropTypes.string,
    clickthrough_url: PropTypes.string,
    playback_ids: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        policy: PropTypes.string.isRequired
      })
    ),
    status: PropTypes.string,
    aspect_ratio: PropTypes.string,
    type: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    dog_modal_id: PropTypes.object
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  smartImageProps: PropTypes.object.isRequired,
  analytics: PropTypes.shape({
    position: PropTypes.number,
    view: PropTypes.string
  }),
  // eslint-disable-next-line react/forbid-prop-types
  muxRef: PropTypes.object,
  badgeProps: PropTypes.shape({
    text: PropTypes.string.isRequired,
    color: PropTypes.oneOf(COLOR_OPTIONS).isRequired
  })
};

CardMediaCarouselFile.defaultProps = {
  badgeProps: null,
  analytics: {},
  muxRef: null
};

const CardMediaCarousel = React.memo(function CardMediaCarousel({
  mediaFiles,
  smartImageProps,
  isInViewport,
  setLazyRef,
  showCaptions,
  isPuppyCard,
  analytics
}) {
  const carouselRef = useRef(null);
  const splideRef = useRef(null);
  const [rightPadding, setRightPadding] = useState(0);
  const [perPage, setPerPage] = useState(1);

  // horizontal padding between carousel items
  const fileSpacing = isPuppyCard ? 0 : 2;

  const splideOptions = {
    ...DEFAULT_SPLIDE_OPTIONS,
    perPage,
    padding: { left: fileSpacing, right: rightPadding },
    gap: IS_LT_LG ? 4 : 8
  };
  useEffect(() => {
    const { offsetWidth } = carouselRef.current;
    const fileWidth = parseInt(smartImageProps.width, 10);
    const fileWidthWithSpacing = fileWidth + fileSpacing;

    // sometimes we can fit multiple media files in one page...whatcha gonna do!
    const filesPerPage = Math.floor(offsetWidth / fileWidthWithSpacing);
    if (filesPerPage > 1) {
      setPerPage(filesPerPage);
    }

    // HACK: Subtracting 1 here will ensure there is no preview of the previous file
    const padding =
      offsetWidth - fileWidthWithSpacing * filesPerPage - fileSpacing - 1;
    setRightPadding(padding);
  }, []);

  return (
    <div
      className={classnames('CardMediaCarousel', { ml4: !isPuppyCard })}
      ref={carouselRef}
    >
      <div
        style={{
          minHeight: smartImageProps.height || '1px',
          minWidth: smartImageProps.width || '1px'
        }}
        ref={setLazyRef}
      >
        {isInViewport && (
          <MediaCarouselSplide
            splideClassName="BreederSearchResultsPhoto h-100 f0"
            leftArrowClassName={classnames(COMMON_ARROW_CLASS_NAME, {
              ml8: !isPuppyCard,
              ml4: isPuppyCard
            })}
            rightArrowClassName={classnames(COMMON_ARROW_CLASS_NAME, 'mr4')}
            mediaFiles={mediaFiles}
            ref={splideRef}
            CarouselMediaComponent={CardMediaCarouselFile}
            smartImageProps={smartImageProps}
            splideOptions={splideOptions}
            showCaptions={showCaptions}
            hasPhotoBullets={isPuppyCard}
            bulletStyle={{ transform: 'scale(.6)', margin: '2px' }}
            analytics={analytics}
          />
        )}
      </div>
    </div>
  );
});

CardMediaCarousel.propTypes = {
  mediaFiles: PropTypes.arrayOf(
    PropTypes.shape({
      ...PhotographPropTypes,
      url: PropTypes.string,
      urls: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  // eslint-disable-next-line react/forbid-prop-types
  smartImageProps: PropTypes.object.isRequired,
  showCaptions: PropTypes.bool,
  isInViewport: PropTypes.bool.isRequired,
  setLazyRef: PropTypes.func,
  isPuppyCard: PropTypes.bool,
  analytics: PropTypes.shape({
    position: PropTypes.number,
    view: PropTypes.string
  })
};

CardMediaCarousel.defaultProps = {
  setLazyRef: null,
  mediaFiles: [],
  showCaptions: false,
  isPuppyCard: false,
  analytics: {}
};

export default withLazyLoading({
  defaultLazy: true
})(CardMediaCarousel);
